import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { GetXsiteSignOnUrlParameters } from '../../../../../../typings/api/integrations/xsite/rest/v1/authentication';
import { TokenService } from '../../../../services/authentication/token.service';
import { XsiteAxiosServiceFactory } from '../../../../services/axios/xsite-axios-service-factory';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { isValidUuidV4 } from '../../../../utils/text/validation';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { Hyperlink } from '../../hyperlink/hyperlink';
import { InfoBox } from '../../info-box/info-box';
import { TextBox } from '../../text-box/text-box';
import { DeleteIntegrationDialog } from '../dialogs/delete-integration-dialog/delete-integration-dialog';
import { IntegrationTitle } from '../integration-title';
import { IntegrationStatus, XsiteIntegrationItem } from '../state/integrations-state';
import { IntegrationGrants } from './integration-grants';
import { resolveIssueCodeTranslation } from './issue-code-to-local-key';
import { Component, Content, Footer, InfoItem, InfoItems } from './style';

enum XsiteIntegrationSetupStatus {
  Idle,
  Pending,
  Error,
}

interface Props {
  item: XsiteIntegrationItem;
}

const XsiteIntegration = (props: Props) => {
  const { t } = useTranslation('integrations');
  const { project } = React.useContext(ProjectContext);
  const [siteId, setSiteId] = React.useState<string>();
  const [status, setStatus] = React.useState(XsiteIntegrationSetupStatus.Idle);
  const { handleError, buildErrorList, includeErrorPaths } =
    useErrorHandling<RequestParamsToApiPaths<GetXsiteSignOnUrlParameters>>();
  const deleteIntegrationDialog = useDialog();

  const setUrl = (url: string) => {
    const id = /\/sites\/(.+?)\//i.exec(url)?.[1];
    if (isValidUuidV4(id)) {
      setSiteId(id);
    } else {
      setSiteId(undefined);
    }
  };

  const signOn = async () => {
    if (status === XsiteIntegrationSetupStatus.Pending) {
      return;
    }
    try {
      setStatus(XsiteIntegrationSetupStatus.Pending);
      const projectId = project.id;
      const token = new TokenService().get();

      const response = await XsiteAxiosServiceFactory.instance
        .createAuthenticationServiceV1()
        .getXsiteSignOnUrl({
          query: {
            skyMapProjectId: projectId,
            successRedirectUrl: window.location.href,
            skyMapAuthenticationToken: token,
            xsiteSiteId: siteId!,
          },
        });

      document.location.assign(response.redirectUrl);
    } catch (err) {
      handleError(err);
      setStatus(XsiteIntegrationSetupStatus.Error);
    }
  };

  const generateXsiteProjectLink = (id: string) => {
    return `https://xsitemanage.com/sites/${id}/explore`;
  };

  switch (props.item.status) {
    case IntegrationStatus.Active:
      return (
        <Component>
          <IntegrationTitle>{props.item.name}</IntegrationTitle>
          <InfoBox
            bottomMargin={true}
            color="yellow"
            leftIcon={{ icon: ['fad', 'info'] }}
            topMargin={true}
            width="fit-content"
          >
            <Trans
              components={{ bold: <strong />, br: <br /> }}
              i18nKey="provider.common.fileManagerInfo"
              ns="integrations"
            />
          </InfoBox>

          <Content>
            <InfoItems>
              <InfoItem>
                <label id="activated">
                  {t('provider.common.activated', { ns: 'integrations' })}
                </label>
                <span aria-labelledby="activated">{formattedDateTime(props.item.createdAt)}</span>
              </InfoItem>

              <InfoItem>
                <label id="projectUrl">
                  {t('provider.common.projectLink', { ns: 'integrations' })}
                </label>
                <span aria-labelledby="projectUrl">
                  <Hyperlink url={generateXsiteProjectLink(props.item.xsiteSiteId ?? '')} />
                </span>
              </InfoItem>
              <IntegrationGrants integration="XSITE" />
            </InfoItems>
          </Content>

          {isDefined(props.item.issues) &&
            props.item.issues.map((issue, index) => (
              <InfoBox
                bottomMargin={true}
                color={
                  issue.severity === 'error'
                    ? 'red'
                    : issue.severity === 'warning'
                      ? 'yellow'
                      : 'blue'
                }
                key={index}
                leftIcon={{ icon: ['fad', 'exclamation'] }}
                topMargin={true}
                width="fit-content"
              >
                {resolveIssueCodeTranslation(issue.code)}
              </InfoBox>
            ))}

          <Footer>
            <Button
              color="primary"
              disabled={!props.item.integrationId}
              loading={status === XsiteIntegrationSetupStatus.Pending}
              variant="contained"
              onClick={() => deleteIntegrationDialog.show()}
            >
              {t('provider.common.removeIntegration', { ns: 'integrations' })}
            </Button>
          </Footer>

          {deleteIntegrationDialog.render(
            <DeleteIntegrationDialog
              item={props.item}
              onClose={() => deleteIntegrationDialog.hide()}
            />,
          )}
        </Component>
      );

    case IntegrationStatus.Setup:
      return (
        <Component>
          <IntegrationTitle>{props.item.name}</IntegrationTitle>

          <InfoBox
            bottomMargin={true}
            color="yellow"
            leftIcon={{ icon: ['fad', 'info'] }}
            topMargin={true}
            width="fit-content"
          >
            {t('provider.xsite.setupInfo', { ns: 'integrations', name: props.item.name })}
          </InfoBox>

          {status === XsiteIntegrationSetupStatus.Error &&
            buildErrorList({
              filter: includeErrorPaths(['query.xsiteSiteId', 'query.skyMapProjectId']),
              defaultMessage: t('provider.common.activateError', { ns: 'integrations' }),
              infoBoxProps: {
                bottomMargin: true,
                width: 'fit-content',
              },
            })}

          <Content>
            <InfoItems>
              <InfoItem>
                <label htmlFor="projectUrl">
                  {t('provider.common.projectLink', { ns: 'integrations' })}
                </label>
                <TextBox
                  disabled={status === XsiteIntegrationSetupStatus.Pending}
                  id="projectUrl"
                  placeholder={generateXsiteProjectLink('238bf4d3-b77e-45b1-840d-eec33bbce1fc')}
                  onChange={(e) => {
                    setUrl(e.target.value);
                  }}
                />
              </InfoItem>
            </InfoItems>
          </Content>

          <Footer>
            <Button
              color="primary"
              disabled={!siteId}
              loading={status === XsiteIntegrationSetupStatus.Pending}
              variant="contained"
              onClick={signOn}
            >
              {t('provider.common.activateIntegration', { ns: 'integrations' })}
            </Button>
          </Footer>
        </Component>
      );
    default:
      return <Component>{t('provider.common.loadError', { ns: 'integrations' })}</Component>;
  }
};

export { XsiteIntegration };
